import React, { useState, useMemo } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../../components/DirectionalIcon';
import { colors, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { BusinessUser } from '../../services/api';
import { useThemedStyles } from '../../hooks/useThemedStyles';

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

// Same role/status → color mapping as UserSetupScreen.tsx's getRoleColor/
// getStatusColor, reused verbatim for visual consistency across screens.
const ROLE_COLOR: Record<string, string> = {
  owner: colors.primary as string,
  admin: colors.warning as string,
  manager: colors.info as string,
  member: colors.success as string,
  viewer: colors.textLight as string,
};
const ROLE_ICON: Record<string, string> = {
  admin: 'shield',
  manager: 'briefcase',
  member: 'person',
  viewer: 'eye',
};
const STATUS_COLOR: Record<string, string> = {
  active: colors.success as string,
  pending: colors.warning as string,
  suspended: colors.error as string,
  left: colors.textLight as string,
};

const PREVIEW_LIMIT = 5;

export interface UsersTeamCardProps {
  users: BusinessUser[];
  onViewAll?: () => void;
  onSettings?: () => void;
}

/** Team/users list — mirrors UserSetupScreen.tsx's renderUserCard structure
 * (avatar initials, owner badge, role/status badges) so this reads as the
 * same visual language elsewhere in the app, not a new pattern. Client-side
 * search only, matching that screen's existing searchQuery approach — no
 * SearchBar component exists in this codebase to reuse instead. Shows only
 * a preview slice — the full list lives in DashboardTeamAllScreen. */
export const UsersTeamCard: React.FC<UsersTeamCardProps> = ({ users, onViewAll, onSettings }) => {
  const styles = useThemedStyles(getStyles);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? users.filter((u) => {
          const name = `${u.user?.firstName ?? ''} ${u.user?.lastName ?? ''}`.toLowerCase();
          return name.includes(q) || (u.user?.email ?? '').toLowerCase().includes(q) || u.role.toLowerCase().includes(q);
        })
      : users;
    return base.slice(0, PREVIEW_LIMIT);
  }, [users, query]);

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <Text style={styles.title}>Team</Text>
        <View style={styles.headerRight}>
          <Text style={styles.count}>{users.length}</Text>
          {onSettings && (
            <TouchableOpacity onPress={onSettings} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="settings-outline" size={16} color={colors.text.secondary as string} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      <View style={styles.searchBox}>
        <Ionicons name="search-outline" size={14} color={colors.text.secondary as string} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search name, email, role"
          placeholderTextColor={colors.text.secondary as string}
          value={query}
          onChangeText={setQuery}
        />
      </View>

      {filtered.length === 0 ? (
        <View style={styles.empty}>
          <Ionicons name="people-outline" size={22} color={colors.text.secondary as string} />
          <Text style={styles.emptyText}>{users.length === 0 ? 'No team members yet' : 'No matches'}</Text>
        </View>
      ) : (
        <View style={styles.list}>
          {filtered.map((u) => (
            <View key={u._id} style={styles.row}>
              <View style={styles.avatar}>
                <Text style={styles.avatarText}>
                  {(u.user?.firstName?.charAt(0) ?? '') || (u.user?.email?.charAt(0) ?? '?')}
                  {u.user?.lastName?.charAt(0) ?? ''}
                </Text>
              </View>
              <View style={styles.rowContent}>
                <View style={styles.nameLine}>
                  <Text style={styles.name} numberOfLines={1}>
                    {u.user?.firstName || u.user?.lastName
                      ? `${u.user?.firstName ?? ''} ${u.user?.lastName ?? ''}`.trim()
                      : (u.user?.email ?? 'Pending invite')}
                  </Text>
                  {u.role === 'owner' && <Ionicons name="star" size={12} color={colors.primary as string} />}
                </View>
                <Text style={styles.email} numberOfLines={1}>{u.user?.email ?? ''}</Text>
              </View>
              <View style={styles.badges}>
                <View style={[styles.badge, { backgroundColor: `${ROLE_COLOR[u.role] ?? colors.textLight}20` }]}>
                  {ROLE_ICON[u.role] && (
                    <Ionicons name={ROLE_ICON[u.role] as any} size={11} color={ROLE_COLOR[u.role] ?? (colors.textLight as string)} />
                  )}
                  <Text style={[styles.badgeText, { color: ROLE_COLOR[u.role] ?? colors.textLight }]}>
                    {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                  </Text>
                </View>
                <View style={styles.statusLine}>
                  <View style={[styles.statusDot, { backgroundColor: STATUS_COLOR[u.userStatus] ?? colors.textLight }]} />
                  <Text style={styles.statusText}>
                    {u.lastActiveAt || u.user.lastLoginAt ? timeAgo(u.lastActiveAt ?? u.user.lastLoginAt!) : u.userStatus}
                  </Text>
                </View>
              </View>
            </View>
          ))}
        </View>
      )}

      {onViewAll && users.length > 0 && (
        <TouchableOpacity style={styles.viewAll} onPress={onViewAll} activeOpacity={0.7}>
          <Text style={styles.viewAllText}>View all</Text>
          <DirectionalIcon name="chevron-forward" size={14} color={colors.brand.primary as string} />
        </TouchableOpacity>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: designTokens.spacing.md,
    ...designTokens.shadows.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: designTokens.spacing.sm,
  },
  title: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
  },
  count: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.bold,
    color: colors.brand.primary,
    backgroundColor: colors.primaryTransparent,
    paddingHorizontal: designTokens.spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.full,
    overflow: 'hidden',
  },
  viewAll: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    marginTop: designTokens.spacing.sm,
    paddingTop: designTokens.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  viewAllText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.brand.primary,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.xs,
    backgroundColor: colors.background.base,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: designTokens.spacing.sm,
    marginBottom: designTokens.spacing.sm,
  },
  searchInput: {
    flex: 1,
    height: 32,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
  },
  list: {
    gap: designTokens.spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.primaryTransparent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.bold,
    color: colors.brand.primary,
  },
  rowContent: {
    flex: 1,
    minWidth: 0,
  },
  nameLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  name: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  email: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  badges: {
    alignItems: 'flex-end',
    gap: 4,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: designTokens.spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.full,
  },
  badgeText: {
    fontSize: 10,
    fontFamily: typography.fontFamily.semibold,
  },
  statusLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontSize: 10,
    color: colors.text.secondary,
  },
  empty: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: designTokens.spacing.xs,
    paddingVertical: designTokens.spacing.lg,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    textAlign: 'center',
  },
});
